import type { SahiConfig } from "@/core/cameraApi/schemas/nn";
import { formatDuration } from "@/lib/utils";
import {
  DirectionPicker,
  ZoneConfig,
  centerThicknessToSafeBounds,
  safeBoundsToCenterThickness,
  safeZoneLabels,
} from "@/modules/dashboard/components/DashboardZoneConfiguration";
import { Label } from "@/modules/shadcn/ui/label";
import { NumberInput } from "@/modules/shadcn/ui/number-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shadcn/ui/select";
import { Switch } from "@/modules/shadcn/ui/switch";
import type { CapturedVideo } from "@repo/schema";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { SahiConfigPanel } from "./SahiConfigPanel";
import { formatDate } from "@/core/i18n";

interface Stream {
  name: string;
}

interface TrainedModel {
  id: number;
  name: string;
}

interface ConfigurationSidebarProps {
  trainedModels: TrainedModel[];
  selectedModelId: string | null;
  onModelChange: (id: string | null) => void;
  onModelClear: () => void;

  zoneConfig: ZoneConfig;
  onZoneConfigChange: (value: ZoneConfig) => void;

  streams: Stream[] | undefined;
  selectedCamera: string | null;
  selectedStream: string | null;
  onStreamChange: (name: string | null) => void;

  capturedVideos: CapturedVideo[];
  selectedVideoId: number | null;
  onVideoChange: (id: number | null) => void;

  sahiConfig: SahiConfig | null;
  onSahiConfigChange: (config: SahiConfig | null) => void;
}

export const ConfigurationSidebar = ({
  trainedModels,
  selectedModelId,
  onModelChange,
  onModelClear,
  zoneConfig,
  onZoneConfigChange,
  streams,
  selectedCamera,
  selectedStream,
  onStreamChange,
  capturedVideos,
  selectedVideoId,
  onVideoChange,
  sahiConfig,
  onSahiConfigChange,
}: ConfigurationSidebarProps) => {
  const { t } = useTranslation(["run", "dashboard", "common"]);
  const selectedVideo =
    selectedVideoId != null
      ? capturedVideos.find((v) => v.id === selectedVideoId)
      : undefined;

  return (
    <aside className="flex w-[360px] shrink-0 flex-col overflow-y-auto border-r bg-card">
      <Section title={t("configuration.sections.model")}>
        <Field label={t("configuration.fields.model")}>
          <div className="relative">
            <Select
              key={selectedModelId ?? "empty"}
              value={selectedModelId ?? undefined}
              onValueChange={onModelChange}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t("common:empty.noneSelected")}>
                  {(value) =>
                    trainedModels.find((m) => String(m.id) === value)?.name ??
                    ""
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {trainedModels.map((model) => (
                  <SelectItem key={model.id} value={String(model.id)}>
                    {model.name}
                  </SelectItem>
                ))}
                {trainedModels.length === 0 && (
                  <p className="px-2 py-1.5 text-sm text-muted-foreground">
                    {t("configuration.noTrainedModels")}
                  </p>
                )}
              </SelectContent>
            </Select>
            {selectedModelId !== null && (
              <ClearButton
                ariaLabel={t("configuration.clearModel")}
                onClick={onModelClear}
              />
            )}
          </div>
        </Field>
      </Section>

      <Section title={t("configuration.sections.safeZones")}>
        <Field label={t("dashboard:zone.direction")}>
          <div className="w-fit">
            <DirectionPicker
              value={zoneConfig.zoneDirection}
              onChange={(zoneDirection) =>
                onZoneConfigChange({ ...zoneConfig, zoneDirection })
              }
            />
          </div>
        </Field>
        {(() => {
          const { safeStartPct, safeEndPct } = centerThicknessToSafeBounds(
            zoneConfig.zoneCenter,
            zoneConfig.zoneThickness,
          );
          const labels = safeZoneLabels(zoneConfig.zoneDirection);
          const applySafeBounds = (nextStart: number, nextEnd: number) => {
            const { centerPct, thicknessPct } = safeBoundsToCenterThickness(
              nextStart,
              nextEnd,
            );
            onZoneConfigChange({
              ...zoneConfig,
              zoneCenter: centerPct,
              zoneThickness: thicknessPct,
            });
          };
          const setSafeStart = (next: number) => {
            const s = Math.max(0, Math.min(100, next));
            const e = Math.min(safeEndPct, 100 - s);
            applySafeBounds(s, e);
          };
          const setSafeEnd = (next: number) => {
            const e = Math.max(0, Math.min(100, next));
            const s = Math.min(safeStartPct, 100 - e);
            applySafeBounds(s, e);
          };
          return (
            <>
              <Field label={t(labels.start)}>
                <PercentInput value={safeStartPct} onChange={setSafeStart} />
              </Field>
              <Field label={t(labels.end)}>
                <PercentInput value={safeEndPct} onChange={setSafeEnd} />
              </Field>
            </>
          );
        })()}
        <div className="flex items-center justify-between gap-3 pt-1">
          <Label
            htmlFor="run-zone-optimistic"
            className="text-sm text-muted-foreground"
          >
            {t("dashboard:zone.optimistic")}
          </Label>
          <Switch
            id="run-zone-optimistic"
            checked={zoneConfig.optimistic}
            onCheckedChange={(checked) =>
              onZoneConfigChange({ ...zoneConfig, optimistic: checked })
            }
          />
        </div>
      </Section>

      <Section title={t("configuration.sections.camera")}>
        <Field label={t("configuration.fields.sensor")}>
          <Select
            value={selectedStream ?? undefined}
            onValueChange={onStreamChange}
            disabled={!selectedCamera}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("configuration.selectSensor")}>
                {selectedStream}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {streams?.map((stream) => (
                <SelectItem key={stream.name} value={stream.name}>
                  {stream.name}
                </SelectItem>
              ))}
              {(!streams || streams.length === 0) && (
                <p className="px-2 py-1.5 text-sm text-muted-foreground">
                  {selectedCamera
                    ? t("configuration.noSensors")
                    : t("configuration.selectCameraInSettings")}
                </p>
              )}
            </SelectContent>
          </Select>
        </Field>
      </Section>

      <Section title={t("configuration.sections.cameraMock")}>
        <Field label={t("configuration.fields.replayVideo")}>
          <div className="relative">
            <Select
              key={selectedVideoId ?? "empty"}
              value={
                selectedVideoId != null ? String(selectedVideoId) : undefined
              }
              onValueChange={(val) => onVideoChange(Number(val))}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t("common:empty.noneSelected")}>
                  {selectedVideoId == null
                    ? undefined
                    : selectedVideo
                      ? formatVideoLabel(selectedVideo)
                      : `#${selectedVideoId}`}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {capturedVideos.map((video) => (
                  <SelectItem key={video.id} value={String(video.id)}>
                    {formatVideoLabel(video)}
                  </SelectItem>
                ))}
                {capturedVideos.length === 0 && (
                  <p className="px-2 py-1.5 text-sm text-muted-foreground">
                    {t("configuration.noCapturedVideos")}
                  </p>
                )}
              </SelectContent>
            </Select>
            {selectedVideoId !== null && (
              <ClearButton
                ariaLabel={t("configuration.clearReplayVideo")}
                onClick={() => onVideoChange(null)}
              />
            )}
          </div>
        </Field>
      </Section>

      <SahiConfigPanel value={sahiConfig} onChange={onSahiConfigChange} />
    </aside>
  );
};

const Section = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <section className="flex flex-col gap-4 border-b px-6 py-5 last:border-b-0">
    <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {title}
    </h2>
    <div className="flex flex-col gap-3">{children}</div>
  </section>
);

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-sm text-muted-foreground">{label}</label>
    {children}
  </div>
);

const PercentInput = ({
  value,
  onChange,
}: {
  value: number;
  onChange: (value: number) => void;
}) => (
  <div className="relative">
    <NumberInput
      value={value}
      min={0}
      max={100}
      onValueChange={(v) => onChange(v ?? 0)}
      className="pr-8"
    />
    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
      %
    </span>
  </div>
);

const ClearButton = ({
  ariaLabel,
  onClick,
}: {
  ariaLabel: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    aria-label={ariaLabel}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    onPointerDown={(e) => e.stopPropagation()}
    className="absolute right-8 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
  >
    <X className="size-3.5" />
  </button>
);

const formatVideoLabel = (video: CapturedVideo): string => {
  const date = formatDate(video.createdAt, "date");
  return `#${video.id} - ${date} - ${formatDuration(video.durationMs)}`;
};
